import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  Rocket,
  ThumbsUp,
  TrendingUp,
  User,
  LogOut,
  ArrowLeft,
  ExternalLink,
  Plus,
  Loader2,
  Settings
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const UserDashboard = () => {
  const { user, logout, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState({ submissions: 0, upvotesGiven: 0, upvotesReceived: 0 });
  const [myStartups, setMyStartups] = useState([]);
  const [upvotedStartups, setUpvotedStartups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, authLoading, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      if (!isAuthenticated) return;
      
      setLoading(true);
      try {
        const [statsRes, startupsRes, upvotesRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/user/stats`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/user/startups`, { withCredentials: true }),
          axios.get(`${BACKEND_URL}/api/user/upvotes`, { withCredentials: true })
        ]);
        
        setStats(statsRes.data);
        setMyStartups(startupsRes.data);
        setUpvotedStartups(upvotesRes.data);
      } catch (error) {
        console.error('Failed to fetch dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, [isAuthenticated]);

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <h1 className="text-xl font-bold text-gray-900">My Dashboard</h1>
            </div>
            <div className="flex items-center gap-4">
              <Link to="/admin" className="text-gray-600 hover:text-gray-900">
                <Settings className="w-5 h-5" />
              </Link>
              <button
                onClick={logout}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-900"
              >
                <LogOut className="w-5 h-5" />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Profile Card */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8">
          <div className="flex items-center gap-4">
            {user?.picture ? (
              <img
                src={user.picture}
                alt={user.name}
                className="w-16 h-16 rounded-full"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <User className="w-8 h-8 text-emerald-600" />
              </div>
            )}
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{user?.name}</h2>
              <p className="text-gray-500">{user?.email}</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-emerald-100 rounded-lg">
                <Rocket className="w-6 h-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">My Submissions</p>
                <p className="text-2xl font-bold text-gray-900">{stats.submissions}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <ThumbsUp className="w-6 h-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Upvotes Given</p>
                <p className="text-2xl font-bold text-gray-900">{stats.upvotesGiven}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-purple-100 rounded-lg">
                <TrendingUp className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Upvotes Received</p>
                <p className="text-2xl font-bold text-gray-900">{stats.upvotesReceived}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="submissions">My Submissions ({myStartups.length})</TabsTrigger>
            <TabsTrigger value="upvoted">Upvoted ({upvotedStartups.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Link
                  to="/"
                  className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-emerald-500 hover:bg-emerald-50 transition-colors"
                >
                  <Plus className="w-5 h-5 text-emerald-600" />
                  <span className="font-medium text-gray-900">Submit a Startup</span>
                </Link>
                <Link
                  to="/"
                  className="flex items-center gap-3 p-4 rounded-lg border border-gray-200 hover:border-blue-500 hover:bg-blue-50 transition-colors"
                >
                  <Rocket className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-gray-900">Browse Startups</span>
                </Link>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="submissions">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
              </div>
            ) : myStartups.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <Rocket className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">You haven't submitted any startups yet.</p>
                <Link to="/">
                  <Button className="bg-emerald-500 hover:bg-emerald-600 text-white">
                    <Plus className="w-4 h-4 mr-2" />
                    Submit Your First Startup
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {myStartups.map((startup) => (
                  <StartupListItem key={startup.id} startup={startup} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="upvoted">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
              </div>
            ) : upvotedStartups.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                <ThumbsUp className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">You haven't upvoted any startups yet.</p>
                <Link to="/">
                  <Button variant="outline">
                    Browse Startups
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="space-y-4">
                {upvotedStartups.map((startup) => (
                  <StartupListItem key={startup.id} startup={startup} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

const StartupListItem = ({ startup }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 hover:border-gray-300 transition-colors">
      <div className="flex items-center gap-4">
        <img
          src={startup.image}
          alt={startup.name}
          className="w-16 h-12 rounded-lg object-cover bg-gray-100"
        />
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-gray-900 truncate">{startup.name}</h4>
          <p className="text-sm text-gray-500 truncate">{startup.description}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-1 text-gray-600">
            <ThumbsUp className="w-4 h-4" />
            <span className="font-medium">{startup.rating}</span>
          </div>
          <Link
            to={`/startup/${startup.slug}`}
            className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
          >
            <ExternalLink className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UserDashboard;
